// Vector annotation primitives. Stored as percentages of the screenshot
// dimensions so they render correctly at any preview/render size, then
// rasterized onto the screenshot blob at original resolution at submit time.

export type Pin = {
  type: "pin";
  id: string;
  // Percent of the captured screenshot — what the dashboard's image card uses.
  xPct: number;
  yPct: number;
  note: string;
};

// Submission-time absolute page coordinates so the widget can re-render pins
// in place on the live page later. Captured once at submit; viewport-relative
// percents above stay for screenshot rendering.
export type PinPageCoords = {
  pageX: number;
  pageY: number;
  pageWidth: number;
  pageHeight: number;
  scrollY: number;
};

export type Rectangle = {
  type: "rectangle";
  id: string;
  x1Pct: number;
  y1Pct: number;
  x2Pct: number;
  y2Pct: number;
  // Stroke thickness as % of min(width,height); omitted = default.
  strokeWidthPct?: number;
};

export type Arrow = {
  type: "arrow";
  id: string;
  x1Pct: number;
  y1Pct: number;
  x2Pct: number;
  y2Pct: number;
  strokeWidthPct?: number;
};

export type FreeDraw = {
  type: "freedraw";
  id: string;
  pointsPct: Array<{ xPct: number; yPct: number }>;
  strokeWidthPct?: number;
};

export type Annotation = Pin | Rectangle | Arrow | FreeDraw;

export const ANNOTATION_COLOR = "#ef4444";
export const ANNOTATION_STROKE_WIDTH_PCT = 0.7; // default: % of min(width,height)

// Selectable thicknesses (% of the screenshot's min side). Stored per-shape so
// changing the picker never resizes shapes already drawn.
export const STROKE_PRESETS = [
  { key: "s", label: "Thin", pct: 0.35 },
  { key: "m", label: "Medium", pct: 0.7 },
  { key: "l", label: "Thick", pct: 1.3 },
] as const;

/** Resolve a shape's stroke width (% of min side), falling back to the default. */
export function strokeWidthPctOf(a: Annotation): number {
  if (a.type === "pin") return ANNOTATION_STROKE_WIDTH_PCT;
  return a.strokeWidthPct ?? ANNOTATION_STROKE_WIDTH_PCT;
}

export function newId(): string {
  return Math.random().toString(36).slice(2, 9);
}

/**
 * Build an SVG path `d` for a freehand stroke using midpoint quadratic
 * smoothing — turns the raw jagged polyline into a smooth curve. Coordinate
 * space agnostic (callers pass percentages or pixels); because the pct→px
 * transform is linear, the same control points render identically in the live
 * SVG preview and the burned-in canvas result. Mirrors the canvas path built
 * in rasterizeAnnotations so preview and result look the same.
 */
export function freeDrawSvgPath(pts: Array<{ x: number; y: number }>): string {
  if (pts.length < 2) return "";
  const d = (n: number) => n.toFixed(2);
  let out = `M ${d(pts[0]!.x)} ${d(pts[0]!.y)}`;
  if (pts.length === 2) {
    return `${out} L ${d(pts[1]!.x)} ${d(pts[1]!.y)}`;
  }
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i]!.x + pts[i + 1]!.x) / 2;
    const my = (pts[i]!.y + pts[i + 1]!.y) / 2;
    out += ` Q ${d(pts[i]!.x)} ${d(pts[i]!.y)} ${d(mx)} ${d(my)}`;
  }
  const last = pts[pts.length - 1]!;
  return `${out} L ${d(last.x)} ${d(last.y)}`;
}

/**
 * Draw vector annotations onto a 2D canvas at the canvas's pixel resolution.
 *
 * Penanda IKUT dibakar, dan itu perubahan dari versi aslinya (18 Sep 2026).
 * Di brightflow penanda digambar ulang oleh dashboard-nya sebagai titik yang
 * bisa diklik, jadi membakarnya akan menghasilkan nomor dobel. Maua tidak punya
 * dashboard itu — kartu di papan lah yang membawa gambarnya. Dengan penanda
 * dilewati, catatan yang ditulis orang ("tombol ini yang macet") menunjuk ke
 * tempat yang tidak pernah terlihat siapa pun: gambarnya polos, dan yang sampai
 * ke kartu cuma hitungannya. Nomornya sengaja sama dengan urutan catatan di
 * badan kartu, supaya "1" di gambar dan "1" di teks menunjuk hal yang sama.
 */
export function rasterizeAnnotations(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  annotations: Annotation[],
): void {
  const minSide = Math.min(width, height);

  ctx.save();
  ctx.strokeStyle = ANNOTATION_COLOR;
  ctx.fillStyle = ANNOTATION_COLOR;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const a of annotations) {
    // Per-shape thickness so each mark keeps the width it was drawn with.
    const stroke = Math.max(2, (minSide * strokeWidthPctOf(a)) / 100);
    ctx.lineWidth = stroke;
    if (a.type === "rectangle") {
      const x = (Math.min(a.x1Pct, a.x2Pct) / 100) * width;
      const y = (Math.min(a.y1Pct, a.y2Pct) / 100) * height;
      const w = (Math.abs(a.x2Pct - a.x1Pct) / 100) * width;
      const h = (Math.abs(a.y2Pct - a.y1Pct) / 100) * height;
      ctx.strokeRect(x, y, w, h);
    } else if (a.type === "arrow") {
      const x1 = (a.x1Pct / 100) * width;
      const y1 = (a.y1Pct / 100) * height;
      const x2 = (a.x2Pct / 100) * width;
      const y2 = (a.y2Pct / 100) * height;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      // Arrowhead — two short lines fanning back from (x2,y2) along the
      // direction of the arrow.
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLen = Math.max(12, stroke * 4);
      const headAngle = Math.PI / 7;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(
        x2 - headLen * Math.cos(angle - headAngle),
        y2 - headLen * Math.sin(angle - headAngle),
      );
      ctx.moveTo(x2, y2);
      ctx.lineTo(
        x2 - headLen * Math.cos(angle + headAngle),
        y2 - headLen * Math.sin(angle + headAngle),
      );
      ctx.stroke();
    } else if (a.type === "freedraw") {
      if (a.pointsPct.length < 2) continue;
      // Same midpoint quadratic smoothing as the live SVG preview (see
      // freeDrawSvgPath) so the burned result matches what the user drew.
      const pts = a.pointsPct.map((p) => ({
        x: (p.xPct / 100) * width,
        y: (p.yPct / 100) * height,
      }));
      ctx.beginPath();
      ctx.moveTo(pts[0]!.x, pts[0]!.y);
      if (pts.length === 2) {
        ctx.lineTo(pts[1]!.x, pts[1]!.y);
      } else {
        for (let i = 1; i < pts.length - 1; i++) {
          const mx = (pts[i]!.x + pts[i + 1]!.x) / 2;
          const my = (pts[i]!.y + pts[i + 1]!.y) / 2;
          ctx.quadraticCurveTo(pts[i]!.x, pts[i]!.y, mx, my);
        }
        const last = pts[pts.length - 1]!;
        ctx.lineTo(last.x, last.y);
      }
      ctx.stroke();
    }
    // Penanda digambar sesudah semua bentuk lain, di bawah.
  }

  // Lingkaran bernomor, digambar PALING AKHIR supaya tidak tertutup kotak atau
  // panah yang kebetulan melintasinya. Ukurannya mengikuti sisi terpendek, jadi
  // ia tetap terbaca pada tangkapan layar ponsel maupun monitor lebar.
  const pins = annotations.filter((a): a is Pin => a.type === "pin");
  if (pins.length > 0) {
    const r = Math.max(11, minSide * 0.016);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `700 ${Math.round(r * 1.15)}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
    ctx.lineWidth = Math.max(2, r * 0.18);
    pins.forEach((pin, i) => {
      const x = (pin.xPct / 100) * width;
      const y = (pin.yPct / 100) * height;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = ANNOTATION_COLOR;
      ctx.fill();
      // Cincin putih: penanda merah di atas tombol merah akan lenyap tanpa ini.
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.fillText(String(i + 1), x, y + r * 0.05);
    });
  }

  ctx.restore();
}

/**
 * Compose a new PNG blob: draw the original screenshot, then rasterize the
 * vector annotations on top at the screenshot's native pixel resolution.
 */
export async function burnAnnotationsToBlob(
  origBlob: Blob,
  annotations: Annotation[],
): Promise<Blob> {
  if (annotations.length === 0) return origBlob;

  const img = await blobToImage(origBlob);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to create 2d canvas context");
  ctx.drawImage(img, 0, 0);
  rasterizeAnnotations(ctx, canvas.width, canvas.height, annotations);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode annotated screenshot"));
          return;
        }
        resolve(blob);
      },
      "image/png",
      0.95,
    );
  });
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load screenshot for annotation"));
    };
    img.src = url;
  });
}
